import {serializeFilters, serializeFields} from 'nutshell-core';

import {api} from 'nutshell-core/api';
import type {FilterObject, EntityListType} from 'nutshell-core/types';

// @ts-expect-error
import {pushNotification} from '../../../notifications';
import {addNotification} from '../../../notifications/index';
// @ts-expect-error
import {refreshUser} from '../../../user';

type BulkEditFilterParams =
    | {
          filter: FilterObject[] | Record<any, any>;
          q: string;
          omittedItems: string[];
          massSelect: boolean;
      }
    | {
          selectedIds: string[];
      };

type SequenceTemplate = {
    id: string;
    name: string;
};

type QueryState = {
    filter: {
        selected: FilterObject[];
    };
    search: {
        selected: string;
    };
};

export const sendBulkEmail = (
    template: SequenceTemplate,
    options: {
        includeIncomplete: boolean;
        includeRecentlyReceivedTemplate: boolean;
    },
    selectedItems: string[],
    selectionIsExcluded: boolean,
    entityType: EntityListType,
    query: QueryState,
    resetSelection: () => void
) => {
    const {includeIncomplete, includeRecentlyReceivedTemplate} = options;

    const postData: Record<string, any> = {};
    postData.entityType = entityType;

    if (includeRecentlyReceivedTemplate) {
        postData.includeRecentRecipients = true;
    }

    // The user has checked the "Select all" button
    // In this case, we'll use solr, otherwise, we're just using the ids themselves.
    if (selectedItems && selectionIsExcluded) {
        // Need to clone here, or else it will have the side effect of
        // permanently adding this filter
        const filters = query.filter.selected;

        if (!includeIncomplete) filters.push({emailTemplate: template.id});

        if (Object.keys(filters).length > 0) {
            postData.filter = serializeFilters(filters);
        }
        if (query && query.search && query.search.selected) {
            postData.q = query.search.selected;
        }
        if (selectedItems.length > 0) {
            postData.omittedItems = selectedItems;
        }
    } else if (selectedItems) {
        postData.includeIncomplete = includeIncomplete;
        postData.ids = selectedItems;
    }

    if (!postData.ids || postData.ids.length > 0) {
        api.post(`emailtemplates/${template.id}/sends`, postData)
            .then(() => {
                addNotification('Email sent', {
                    body: template.name,
                    icon: 'email',
                });
            })
            .then(() => resetSelection())
            .catch(() =>
                addNotification('Email failed', {
                    // @ts-expect-error
                    type: 'error',
                    message: 'Unable to send email.',
                })
            );
    }
};

export const getBulkEditFilterParams = (
    selectionIsExcluded: boolean,
    selectedItems: string[],
    filters: FilterObject[],
    search: string
): BulkEditFilterParams => {
    // If we just have a list of excluded items, or we want all items (used
    // for exporting when no items are selected), we need to send the filters
    if (selectionIsExcluded || selectedItems.length === 0) {
        return {
            filter: serializeFilters(filters),
            q: search,
            omittedItems: selectedItems,
            massSelect: true,
        };
    } else {
        return {
            selectedIds: selectedItems,
        };
    }
};

export const exportSelectedContacts = (
    bulkEditFilterParams: BulkEditFilterParams,
    selectedFields: string[],
    resetSelection: () => void,
    pollForQueuedExports: () => void
) => {
    const data = {
        ...bulkEditFilterParams,
        fields: serializeFields({contacts: selectedFields}),
    } as const;

    api.get('contacts/list', data, {
        headers: new Headers({Accept: 'text/csv; charset=utf-8'}),
    })
        .then(() => {
            const userId = $('body').data('loggedInUser');
            refreshUser(userId);
            resetSelection();
        })
        .catch(() => pushNotification({type: 'error', message: 'Export failed.'}));

    pollForQueuedExports();
};
